import { useMemo, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Icon, Tabs, TabsContent, TabsList, TabsTrigger } from '@tutero/ui';

import { PageSlots } from './listing/ListPage';
import { PageTitle } from './PageTitle';
import type { TabbedPage } from './tools';

/**
 * The tab bar over a tool that is one tab of a page (`learning-backend-is-one-page` D1).
 * Each tab is a link to its tool's own address, so the page is still that tool: its grant,
 * its lazy chunk, its boundary and its deep links are all as they were, and no tool knows
 * the others exist (A3). `manual`, so an arrow key moves the focus along the bar without
 * opening every tab it passes; the address alone says which tab is open.
 */
export function SectionTabs({
  page,
  active,
  children,
}: {
  page: TabbedPage;
  /** The open tool's id. */
  active: string;
  children: ReactNode;
}) {
  /* State rather than refs, so a list draws into each as soon as it is mounted (D6). */
  const [add, setAdd] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState<HTMLElement | null>(null);
  const slots = useMemo(() => ({ add, search }), [add, search]);
  return (
    <Tabs
      value={active}
      activationMode="manual"
      className="flex min-w-0 flex-1 flex-col gap-4"
    >
      {/* The same heading, at the same size, as every other page (D5), with the open list's Add (D6). */}
      <div className="flex min-h-9 items-center justify-between gap-3">
        <PageTitle icon={page.icon}>{page.title}</PageTitle>
        <div ref={setAdd} className="shrink-0" />
      </div>
      {/* The search wraps under the tabs where the column is too narrow for both, and the tabs
          scroll sideways where it is too narrow for them alone, rather than widening the page. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <TabsList
          variant="underline"
          aria-label={page.title}
          className="max-w-full min-w-0 shrink-0 justify-start overflow-x-auto"
        >
          {page.tools.map((tool) => (
            <TabsTrigger key={tool.id} value={tool.id} asChild>
              <Link to="/$tool" params={{ tool: tool.id }}>
                <Icon name={tool.icon} set="classic" size={18} />
                {tool.title}
              </Link>
            </TabsTrigger>
          ))}
        </TabsList>
        <div ref={setSearch} className="flex min-w-48 flex-1 justify-end" />
      </div>
      <TabsContent value={active} className="flex min-w-0 flex-col">
        <PageSlots value={slots}>{children}</PageSlots>
      </TabsContent>
    </Tabs>
  );
}
